import React, { useMemo, useState } from 'react';
import { Button } from '@material-ui/core';
import clsx from 'clsx';
import DeviceStatusBadge from '../DeviceStatusBadge';
import HardwareStatusesDialog from './HardwareStatusesDialog';
import useStyles from './styles';
import {
  HardwareDeviceStatus,
  HardwareStatusSummary,
} from '../../types/hardwareStatus';
import {
  getCollapsedHardwareStatuses,
  getDeviceStatusIcon,
  getHardwareStatusIcon,
  getLockStatusIcon,
} from '../../utils/hardwareStatus';

type Props = {
  displayMode?: 'default' | 'hardware-list';
  hardwareStatuses?: HardwareDeviceStatus[];
  deviceStatus?: HardwareStatusSummary;
  lockStatus?: HardwareStatusSummary;
};

const HardwareStatusBadges: React.FC<Props> = ({
  displayMode = 'default',
  hardwareStatuses = [],
  deviceStatus,
  lockStatus,
}: Props) => {
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const collapsedStatuses = useMemo(
    () => getCollapsedHardwareStatuses(hardwareStatuses, {
      includePanel: displayMode !== 'hardware-list',
    }),
    [displayMode, hardwareStatuses],
  );

  const isHardwareList = displayMode === 'hardware-list';

  if (hardwareStatuses.length) {
    return (
      <>
        <div
          className={clsx(classes.statusList, {
            [classes.hardwareListStatusList]: isHardwareList,
          })}
        >
          {collapsedStatuses.primary.map((hardware) => (
            <DeviceStatusBadge
              iconOnly={isHardwareList}
              key={`${hardware.deviceId}-${hardware.deviceType}`}
              label={`${hardware.deviceName}: ${hardware.label}`}
              variant={hardware.variant}
              icon={getHardwareStatusIcon(hardware)}
            />
          ))}
          {collapsedStatuses.remaining.length > 0 && (
            <Button
              aria-haspopup="dialog"
              className={classes.showMoreButton}
              onClick={() => setIsModalOpen(true)}
              size="small"
            >
              Show more (
              {collapsedStatuses.remaining.length}
)
            </Button>
          )}
        </div>
        {collapsedStatuses.remaining.length > 0 && (
          <HardwareStatusesDialog
            hardwareStatuses={hardwareStatuses}
            onClose={() => setIsModalOpen(false)}
            open={isModalOpen}
          />
        )}
      </>
    );
  }

  return (
    <div className={classes.statusList}>
      {!isHardwareList && deviceStatus && (
        <DeviceStatusBadge
          label={`System: ${deviceStatus.label}`}
          variant={deviceStatus.variant}
          icon={getDeviceStatusIcon(deviceStatus)}
        />
      )}
      {lockStatus && lockStatus.status !== 'not_available' && (
        <DeviceStatusBadge
          iconOnly={isHardwareList}
          label={`Door Lock: ${lockStatus.label}`}
          variant={lockStatus.variant}
          icon={getLockStatusIcon(lockStatus)}
        />
      )}
    </div>
  );
};

export default React.memo(HardwareStatusBadges);
